import React from 'react';
import {
    Button,
    IconButton,
    TextField,
    Typography,
    Grid,
    InputAdornment
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

const isSafeText = (value) => {
    const unsafePattern = /[<>/"'\\]/; // disallowed characters
    return !unsafePattern.test(value);
};

export default function ExtraToppingsInput(props) {

    const { extraToppings, setExtraToppings, submitError } = props

    const handleChange = (index, field, value) => {
        const updated = [...extraToppings];
        updated[index][field] = value;
        const hasName = String(updated[index].topping_name ?? '').trim() !== '';

        // Extra toppings are optional: only validate when user has entered a name
        const errors = { ...updated[index].errors, [field]: '' };

        if (field === 'topping_name') {
            if (!value.trim()) {
                errors.topping_name = '';
                errors.topping_price = '';
                errors.topping_available_quantity = '';
            } else if (!isSafeText(value)) {
                errors.topping_name = 'Invalid characters';
            }
        } else if (field === 'topping_available_quantity') {
            if (!hasName) {
                errors.topping_available_quantity = '';
            } else if (!String(value).trim()) {
                errors.topping_available_quantity = 'Required';
            } else if (isNaN(Number(value))) {
                errors.topping_available_quantity = 'Must be a number';
            }
        }

        updated[index].errors = errors;
        setExtraToppings(updated);
    };

    const formatPrice = (raw = '') => {
        const digitsOnly = String(raw).replace(/\D/g, '');
        if (!digitsOnly) return '0.00';
        const padded = digitsOnly.padStart(3, '0');
        const intPart = padded.slice(0, -2);
        const decimalPart = padded.slice(-2);
        return `${parseInt(intPart, 10)}.${decimalPart}`;
    };

    const handlePriceChange = (index, value) => {
        const updated = [...extraToppings];
        const digitsOnly = value.replace(/\D/g, '').slice(0, 5);
        updated[index].topping_price = digitsOnly;
        const hasName = String(updated[index].topping_name ?? '').trim() !== '';

        const numeric = parseFloat(formatPrice(digitsOnly));
        updated[index].errors = {
            ...updated[index].errors,
            topping_price:
                !hasName
                    ? ''
                    : !digitsOnly
                        ? 'Required'
                        : numeric > 100.99
                            ? 'Max $100.99 allowed'
                            : '',
        };

        setExtraToppings(updated);
    };

    const handleAdd = () => {
        setExtraToppings([
            ...extraToppings,
            { topping_name: '', topping_price: '', topping_available_quantity: '', errors: {} },
        ]);
    };

    const handleRemove = (index) => {
        const updated = extraToppings.filter((_, i) => i !== index);
        setExtraToppings(updated);
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            width: '600px', marginTop: '10px',
            alignItems: 'start',
        }}>
            <Typography variant="h6" sx={{ mt: 1, mb: 0 }}>
                Extra Toppings
            </Typography>
            {submitError && (
                <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5, mb: 0.5 }}>
                    {submitError}
                </Typography>
            )}
            <div style={{ marginBottom: '20px', color: 'gray', fontSize: '12px' }}>Enter topping name (e.g., Cheese)</div>

            {extraToppings.map((topping, index) => (
                <div style={{
                    display: 'flex', flexDirection: 'row',
                }}>

                    <TextField
                        fullWidth
                        label="Topping Name"
                        value={topping.topping_name}
                        onChange={(e) => handleChange(index, 'topping_name', e.target.value)}
                        error={!!topping.errors.topping_name}
                        helperText={
                            topping.errors.topping_name
                        }
                        InputLabelProps={{
                            sx: {
                                color: 'gray',
                                display: 'flex',
                                '&.Mui-focused': {
                                    color: 'rgb(255, 123, 41)',
                                },
                            },
                        }}
                        sx={{
                            width: '250px',
                            marginBottom: '10px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                                backgroundColor: '#fff',
                                '&:hover fieldset': {
                                    borderColor: 'rgb(24, 20, 54)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgb(24, 20, 54)',
                                },
                            },
                        }}
                    />


                    <TextField
                        label="Price"
                        value={formatPrice(topping.topping_price)}
                        onChange={(e) => handlePriceChange(index, e.target.value)}
                        error={!!topping.errors.topping_price}
                        helperText={topping.errors.topping_price}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputMode: 'numeric',
                        }}
                        InputLabelProps={{
                            sx: {
                                color: 'gray',
                                display: 'flex',
                                '&.Mui-focused': {
                                    color: 'rgb(255, 123, 41)',
                                },
                            },
                        }}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100px',
                            margin: '0px 10px 10px 10px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                                backgroundColor: '#fff',
                                '&:hover fieldset': {
                                    borderColor: 'rgb(24, 20, 54)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgb(24, 20, 54)',
                                },
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        inputProps={{ min: 0 }}
                        value={topping.topping_available_quantity}
                        onChange={(e) => handleChange(index, 'topping_available_quantity', e.target.value)}
                        error={!!topping.errors.topping_available_quantity}
                        helperText={topping.errors.topping_available_quantity}
                        InputLabelProps={{
                            sx: {
                                color: 'gray',
                                display: 'flex',
                                '&.Mui-focused': {
                                    color: 'rgb(255, 123, 41)',
                                },
                            },
                        }}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100px',
                            margin: '0px 0px 10px 10px',
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                                backgroundColor: '#fff',
                                // width: 320,
                                '&:hover fieldset': {
                                    borderColor: 'rgb(24, 20, 54)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgb(24, 20, 54)',
                                },
                            },
                        }}
                    />

                    <Grid item xs={2}>
                        {extraToppings.length > 1 && (
                            <IconButton onClick={() => handleRemove(index)} color="error">
                                <Delete />
                            </IconButton>
                        )}
                    </Grid>
                </div>
            ))}

            <Button variant="contained" startIcon={<Add />} onClick={handleAdd}
                sx={{
                    mt: 2, mb: 2, width: '180px',
                    color: 'rgb(24, 20, 54)',
                    backgroundColor: '#EAEFEF',
                    '&:hover': {
                        backgroundColor: 'rgb(24, 20, 54)', // hover background
                        color: 'white', // optional: text color on hover
                    },
                }}
            >
                Add More
            </Button>

            {/* <Box mt={3}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    Data to send to API:
                </Typography>
                <pre>{JSON.stringify(formatForAPI(), null, 2)}</pre>
            </Box> */}
        </div>
    );
}